/**
 * Prompt Library service for managing prompts.
 * Backed by Firestore via the shared prompts repository; exported function
 * signatures are unchanged from the REST era so screens/controllers are
 * unaffected. userId/organizationId params are accepted but derived from the
 * signed-in Firebase user where needed.
 */

import { Prompt } from '../../domain/models/Prompt';
import {
  fetchPrompts,
  fetchPromptById,
  fetchFavoritePrompts,
  fetchHiddenPromptIds,
  createPrompt,
  createGlobalPrompt,
  updatePromptDoc,
  hidePrompt,
  createDuplicate,
  fetchDuplicate,
  findUserDuplicate,
  updateDuplicate,
  deleteDuplicate,
} from '../../../../shared/data/promptsRepo';

/**
 * Get all prompts
 * @param {Object} filters - Filter options (category, search, organizationId, promptType)
 * @returns {Promise<Array<Prompt>>} Array of prompts
 */
export const getAllPrompts = async (filters = {}) => {
  try {
    const data = await fetchPrompts({
      promptType: filters.promptType || 'all',
      category: filters.category,
      search: filters.search,
      activityTypeId: filters.activityTypeId,
    });
    return data.map((p) => Prompt.fromData(p));
  } catch (error) {
    console.error('Error fetching prompts:', error);
    throw error;
  }
};

/**
 * Get prompt by ID
 * @returns {Promise<Prompt>} Prompt object
 */
export const getPromptById = async (promptId) => {
  try {
    const data = await fetchPromptById(promptId);
    if (!data) {
      throw new Error('Prompt not found');
    }
    return Prompt.fromData(data);
  } catch (error) {
    console.error('Error fetching prompt:', error);
    throw error;
  }
};

/**
 * Get prompt with the current user's duplicate (if any).
 * @returns {Promise<Object>} Object with prompt, duplicate, and hasDuplicate
 */
export const getPromptWithDuplicate = async (promptId) => {
  try {
    const [data, duplicate] = await Promise.all([
      fetchPromptById(promptId),
      findUserDuplicate(promptId),
    ]);
    if (!data) {
      throw new Error('Prompt not found');
    }
    return {
      prompt: Prompt.fromData(data),
      duplicate: duplicate ? Prompt.fromData(duplicate) : null,
      hasDuplicate: !!duplicate,
    };
  } catch (error) {
    console.error('Error fetching prompt with duplicate:', error);
    throw error;
  }
};

/**
 * Duplicate a prompt for the current user.
 * @returns {Promise<Prompt>} Duplicate prompt object
 */
export const duplicatePrompt = async (promptId) => {
  try {
    const data = await createDuplicate(promptId);
    return Prompt.fromData(data);
  } catch (error) {
    console.error('Error duplicating prompt:', error);
    throw error;
  }
};

/**
 * Get duplicate prompt by ID
 * @returns {Promise<Prompt>} Duplicate prompt object
 */
export const getDuplicatePrompt = async (duplicateId) => {
  try {
    const data = await fetchDuplicate(duplicateId);
    return Prompt.fromData(data);
  } catch (error) {
    console.error('Error fetching duplicate prompt:', error);
    throw error;
  }
};

/**
 * Update duplicate prompt
 * @returns {Promise<Prompt>} Updated duplicate prompt
 */
export const updateDuplicatePrompt = async (duplicateId, updates) => {
  try {
    const data = await updateDuplicate(duplicateId, updates);
    return Prompt.fromData(data);
  } catch (error) {
    console.error('Error updating duplicate prompt:', error);
    throw error;
  }
};

/**
 * Delete duplicate prompt
 * @returns {Promise<Object>} Result with success status
 */
export const deleteDuplicatePrompt = async (duplicateId) => {
  try {
    await deleteDuplicate(duplicateId);
    return { success: true };
  } catch (error) {
    console.error('Error deleting duplicate prompt:', error);
    throw error;
  }
};

/**
 * Get top prompts by upvotes
 * @returns {Promise<Array<Prompt>>} Array of top prompts
 */
export const getTopPrompts = async (limit = 10) => {
  const prompts = await getAllPrompts();
  return prompts
    .sort((a, b) => b.getNetVotes() - a.getNetVotes())
    .slice(0, limit);
};

/**
 * Get user's favorite prompts
 * @returns {Promise<Array<Prompt>>} Array of favorite prompts
 */
export const getFavoritePrompts = async () => {
  try {
    const data = await fetchFavoritePrompts();
    return data.map((p) => Prompt.fromData(p));
  } catch (error) {
    console.error('Error fetching favorite prompts:', error);
    throw error;
  }
};

/**
 * Get user's own prompts (excluding ones they've hidden).
 * @returns {Promise<Array<Prompt>>} Array of user's prompts
 */
export const getUserPrompts = async (userId) => {
  const prompts = await getAllPrompts();
  const userPrompts = prompts.filter((p) => p.creatorId === userId);
  try {
    const hiddenIds = await fetchHiddenPromptIds();
    const hiddenSet = new Set(hiddenIds);
    return userPrompts.filter((p) => !hiddenSet.has(p.id));
  } catch (error) {
    console.error('Error fetching hidden prompts:', error);
    return userPrompts;
  }
};

/**
 * Get user interactions for a prompt
 * @returns {Promise<Object>} Interaction status object
 */
export const getUserPromptInteractions = async (userId, organizationId, promptId) => {
  try {
    const prompt = await getPromptById(promptId);
    return {
      upvoted: prompt.hasUpvoted || false,
      downvoted: prompt.hasDownvoted || false,
      favorited: prompt.isFavorite || false,
    };
  } catch (error) {
    console.error('Error fetching user interactions:', error);
    return {
      upvoted: false,
      downvoted: false,
      favorited: false,
    };
  }
};

/**
 * Add prompt to library
 * @returns {Promise<Prompt>} Created prompt
 */
export const addPromptToLibrary = async (promptData) => {
  try {
    const data = await createPrompt(promptData);
    return Prompt.fromData(data);
  } catch (error) {
    console.error('Error adding prompt:', error);
    throw error;
  }
};

/**
 * Add global prompt to library (admin only; enforced by Firestore rules)
 * @returns {Promise<Prompt>} Created global prompt
 */
export const addGlobalPromptToLibrary = async (promptData) => {
  try {
    const data = await createGlobalPrompt(promptData);
    return Prompt.fromData(data);
  } catch (error) {
    console.error('Error adding global prompt:', error);
    throw error;
  }
};

/**
 * Update prompt
 * @returns {Promise<Prompt>} Updated prompt
 */
export const updatePrompt = async (promptId, updates) => {
  try {
    const data = await updatePromptDoc(promptId, updates);
    return Prompt.fromData(data);
  } catch (error) {
    console.error('Error updating prompt:', error);
    throw error;
  }
};

/**
 * Delete prompt (hides it from the user's own prompts list)
 * @returns {Promise<Object>} Result with success status
 */
export const deletePrompt = async (promptId) => {
  try {
    await hidePrompt(promptId);
    return { success: true };
  } catch (error) {
    console.error('Error deleting prompt:', error);
    throw error;
  }
};
